"use client"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import type { NumberFormatDisplay } from "@opensea/i18n"
import { type CurrencyPreference, useCurrency } from "@opensea/navigation"
import {
  AnimatedNumber,
  NumberDisplay,
  textColorVariants,
  type VariantProps,
} from "@opensea/ui-kit"
import { type Nullable, UnreachableCaseError } from "@opensea/utils"
import { VolumeFragment } from "./Volume.fragment"

export { VolumeFragment }

type Props = {
  animated?: boolean
  volume: Nullable<FragmentOf<typeof VolumeFragment>>
  display?: NumberFormatDisplay
  symbolColor?: VariantProps<typeof textColorVariants>["color"]
  compactionThreshold?: number
  custom?: Intl.NumberFormatOptions
  multiplier?: number
  currencyOverride?: CurrencyPreference
}

export function Volume({
  animated,
  volume: key,
  display = "compact",
  symbolColor = "text-secondary",
  compactionThreshold,
  custom,
  multiplier = 1,
  currencyOverride,
}: Props) {
  const volume = readFragment(VolumeFragment, key)

  const { currency } = useCurrency()
  const resolvedCurrency = currencyOverride ?? currency

  const NumberComponent = animated ? AnimatedNumber : NumberDisplay

  switch (resolvedCurrency) {
    case "crypto":
      return (
        <>
          <NumberComponent
            compactionThreshold={compactionThreshold}
            custom={custom}
            display={display}
            value={
              volume?.native.unit !== undefined
                ? volume.native.unit * multiplier
                : undefined
            }
          />

          {volume?.native.symbol ? (
            <span
              className={textColorVariants({
                color: symbolColor,
                className: "font-mono",
              })}
            >
              &nbsp;{volume.native.symbol}
            </span>
          ) : null}
        </>
      )

    case "usd":
      return (
        <NumberComponent
          compactionThreshold={compactionThreshold}
          custom={custom}
          display="usd"
          value={
            volume?.usd !== undefined ? volume.usd * multiplier : undefined
          }
        />
      )
    default:
      throw new UnreachableCaseError(resolvedCurrency)
  }
}
